import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { UserRoleEnum, useMenuContext, useUserContext, type IMenuOption } from '../shared/contexts';
import { Forbidden } from '../shared/components/SconApp/forbidden/Forbidden';
import { AdminBusinesses, AdminDashboard, Appointment, Business, BusinessPublic, ConfirmEmail, CreateBusiness, CreateBusinessTrial, CreateProfessional, Employee, Home, Onboarding, Profile, Landing, SconAppWhatsApp, Schedule, ForgotPassword, ResetPassword, Notifications, SubscriptionCheckout, SubscriptionSuccess, SubscriptionPending } from '../pages';
import {
  BUSINESS_TAB_CONFIG,
  ALL_BUSINESS_TABS,
  RECEPTIONIST_TABS,
} from '../shared/constants/businessTabs';
import { EmployeeTabsEnum } from '../pages/employee/Employee';

export const AppRoutes: React.FC = () => {
  const { setMenuOptions } = useMenuContext();
  const { userRoles } = useUserContext();

  useEffect(() => {
    const menuOptions: IMenuOption[] = [
      {
        icon: 'person',
        label: 'Perfil',
        path: '/profile'
      }
    ];

    const isReceptionist = userRoles.includes(UserRoleEnum.Receptionist);
    const hasRoles = userRoles.includes(UserRoleEnum.Employee) || userRoles.includes(UserRoleEnum.BusinessAdmin) || isReceptionist;

    if (hasRoles) {
      menuOptions.push(
        {
          icon: 'calendar_month',
          label: 'Agenda',
          path: '/schedule'
        },
        {
          icon: 'notifications',
          label: 'Notificações',
          path: '/notifications'
        }
      );
    }

    if (userRoles.includes(UserRoleEnum.Employee) || isReceptionist) {
      menuOptions.push(
        {
          icon: 'engineering',
          label: 'Área do profissional',
          path: '/employee',
          children: [
            { icon: 'business', label: 'Informações', path: '/employee', page: EmployeeTabsEnum.Information },
            { icon: 'receipt', label: 'Comandas', path: '/employee', page: EmployeeTabsEnum.OrderTickets },
            { icon: 'bar_chart', label: 'Dashboard', path: '/employee', page: EmployeeTabsEnum.Dashboard },
            { icon: 'account_balance_wallet', label: 'Carteira', path: '/employee', page: EmployeeTabsEnum.Wallet },
            { icon: 'percent', label: 'Comissões', path: '/employee', page: EmployeeTabsEnum.Commissions },
          ],
        }
      );
    }

    if (userRoles.includes(UserRoleEnum.BusinessAdmin) || isReceptionist) {
      const tabs = isReceptionist
        ? ALL_BUSINESS_TABS.filter(t => RECEPTIONIST_TABS.has(t))
        : [...ALL_BUSINESS_TABS];

      menuOptions.push({
        icon: 'apartment',
        label: 'Meu estabelecimento',
        path: '/business',
        children: tabs.map(t => ({
          icon: BUSINESS_TAB_CONFIG[t].icon,
          label: BUSINESS_TAB_CONFIG[t].label,
          path: '/business',
          page: t,
        })),
      });
    }

    if (userRoles.includes(UserRoleEnum.SystemAdmin)) {
      menuOptions.push(
        {
          icon: 'bar_chart',
          label: 'Dashboard',
          path: '/admin'
        },
        {
          icon: 'apartment',
          label: 'Estabelecimentos',
          path: '/admin/businesses'
        },
        {
          icon: 'person',
          label: 'Criar estabelecimento',
          path: '/create-business'
        },
        {
          icon: 'phone',
          label: 'WhatsApp SconApp',
          path: '/sconapp-whatsapp'
        }
      );
    }

    setMenuOptions(menuOptions);
  }, [userRoles]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/appointment" element={<Appointment />} />
      <Route path="/appointment/:businessId" element={<Appointment />} />
      <Route path="/appointment/:businessId/:businessEmployeeId" element={<Appointment />} />
      <Route path="/confirm-email/:email/:token" element={<ConfirmEmail />} />
      <Route path="/business" element={<Business />} />
      <Route path="/employee" element={<Employee />} />
      <Route path="/create-business" element={<CreateBusiness />} />
      <Route path="/create-business-trial" element={<CreateBusinessTrial />} />
      <Route path="/subscription/checkout" element={<SubscriptionCheckout />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/pending" element={<SubscriptionPending />} />
      <Route path="/home" element={<Home />} />
      <Route path="/invite-employee/:businessId" element={<CreateProfessional />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/businesses" element={<AdminBusinesses />} />
      <Route path="/sconapp-whatsapp" element={<SconAppWhatsApp />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/forbidden" element={<Forbidden />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />

      {/* Public business page — must come after all named routes */}
      <Route path="/:slug" element={<BusinessPublic />} />

      <Route path="*" element={<Navigate to="/home" />}></Route>
    </Routes>
  );
};